import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Network,
  Plus,
  Search,
  RefreshCw,
  PlayCircle,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Activity,
  Eye,
  Pencil,
  Building2,
  CheckCircle,
  Clock
} from 'lucide-react';

export default function Networks() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wbsElements, setWbsElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewEditDialog, setShowViewEditDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');

  const [formData, setFormData] = useState({
    network_id: '',
    project_id: '',
    wbs_element_id: '',
    description: '',
    start_date: '',
    finish_date: '',
    responsible_person_id: ''
  });

  const [editFormData, setEditFormData] = useState({
    description: '',
    basic_start_date: '',
    basic_finish_date: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [networksRes, projectsRes] = await Promise.all([
        psAPI.getNetworks(),
        psAPI.getProjects()
      ]);
      setNetworks(networksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load networks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchWBSElements = async (projectId) => {
    try {
      const response = await psAPI.getWBSElements({ project_id: projectId });
      setWbsElements(response.data);
    } catch (error) {
      console.error('Failed to load WBS elements', error);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData({ ...formData, project_id: projectId, wbs_element_id: '' });
    if (projectId && projectId !== '__none__') {
      fetchWBSElements(projectId);
    } else {
      setWbsElements([]);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = { ...formData };
      if (!payload.network_id) delete payload.network_id;
      if (!payload.responsible_person_id) delete payload.responsible_person_id;
      
      await psAPI.createNetwork(payload);
      toast.success('Network created successfully');
      setShowCreateDialog(false);
      setFormData({
        network_id: '',
        project_id: '',
        wbs_element_id: '',
        description: '',
        start_date: '',
        finish_date: '',
        responsible_person_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create network');
    }
  };

  const handleRelease = async (networkId) => {
    try {
      await psAPI.releaseNetwork(networkId);
      toast.success('Network released successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release network');
    }
  };

  const handleView = async (network) => {
    try {
      const response = await psAPI.getNetwork(network.network_id);
      setSelectedNetwork(response.data);
      setEditFormData({
        description: response.data.description || '',
        basic_start_date: response.data.basic_start_date || '',
        basic_finish_date: response.data.basic_finish_date || ''
      });
      setIsEditMode(false);
      setShowViewEditDialog(true);
    } catch (error) {
      toast.error('Failed to load network details');
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        description: editFormData.description || undefined,
        basic_start_date: editFormData.basic_start_date || undefined,
        basic_finish_date: editFormData.basic_finish_date || undefined
      };
      
      await psAPI.updateNetwork(selectedNetwork.network_id, payload);
      toast.success('Network updated successfully');
      setShowViewEditDialog(false);
      setSelectedNetwork(null);
      setIsEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update network');
    }
  };

  const filteredNetworks = networks.filter(n => {
    const matchesSearch = !searchQuery || 
      n.network_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = filterProject === '__all__' || n.project_id === filterProject;
    const matchesStatus = filterStatus === '__all__' || n.network_status === filterStatus;
    return matchesSearch && matchesProject && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      'CREATED': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      'RELEASED': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'PARTIALLY_CONFIRMED': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      'CONFIRMED': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      'TECO': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'CLOSED': { bg: 'bg-slate-50', text: 'text-slate-600', dot: 'bg-slate-400' }
    };
    const c = config[status] || config['CREATED'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  // Calculate stats
  const stats = {
    total: networks.length,
    created: networks.filter(n => n.network_status === 'CREATED').length,
    released: networks.filter(n => n.network_status === 'RELEASED').length,
    confirmed: networks.filter(n => n.network_status === 'CONFIRMED' || n.network_status === 'PARTIALLY_CONFIRMED').length,
    totalActivities: networks.reduce((sum, n) => sum + (n.activity_count || 0), 0)
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="networks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ps/execution')} className="hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-lg">
                <Network className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Networks</h1>
                <p className="text-sm text-slate-500">Manage project networks and their activities</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canWrite('project_system') && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="create-network-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Network
            </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Networks</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <Network className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Created</p>
                  <p className="text-2xl font-bold text-slate-700">{stats.created}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <Clock className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Released</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.released}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <PlayCircle className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-600">Confirmed</p>
                  <p className="text-2xl font-bold text-emerald-700">{stats.confirmed}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Total Activities</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.totalActivities}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Activity className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search networks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200"
                  data-testid="search-networks"
                />
              </div>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200" data-testid="filter-project">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] bg-slate-50 border-slate-200" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Status</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="RELEASED">Released</SelectItem>
                  <SelectItem value="PARTIALLY_CONFIRMED">Partially Confirmed</SelectItem>
                  <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                  <SelectItem value="TECO">Technically Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Networks Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Network ID</TableHead>
                <TableHead className="font-semibold text-slate-600">Project</TableHead>
                <TableHead className="font-semibold text-slate-600">WBS Element</TableHead>
                <TableHead className="font-semibold text-slate-600">Description</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                <TableHead className="font-semibold text-slate-600">Activities</TableHead>
                <TableHead className="font-semibold text-slate-600">Progress</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2 text-slate-500">
                    <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>
                    Loading...
                  </div>
                </TableCell></TableRow>
              ) : filteredNetworks.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <Network className="h-10 w-10 text-slate-300" />
                    <p className="text-slate-500">No networks found</p>
                    {canWrite('project_system') && (
                    <Button onClick={() => setShowCreateDialog(true)} variant="link" className="mt-2">
                      Create your first network
                    </Button>
                    )}
                  </div>
                </TableCell></TableRow>
              ) : filteredNetworks.map((network) => (
                <TableRow key={network.network_id} className="group hover:bg-orange-50/50 transition-colors" data-testid={`network-row-${network.network_id}`}>
                  <TableCell>
                    <span className="font-mono text-sm font-medium text-orange-600">{network.network_id}</span>
                  </TableCell>
                  <TableCell className="text-slate-600">{network.project_name || network.project_id}</TableCell>
                  <TableCell className="text-slate-600">{network.wbs_element_name || network.wbs_element_id}</TableCell>
                  <TableCell className="text-slate-600">{network.description || '-'}</TableCell>
                  <TableCell>{getStatusBadge(network.network_status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Activity className="w-4 h-4 text-slate-400" />
                      <span>{network.activity_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-200 rounded-full h-1.5">
                        <div className="bg-orange-600 h-1.5 rounded-full" style={{ width: `${network.overall_progress || 0}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{network.overall_progress || 0}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => handleView(network)} data-testid={`view-${network.network_id}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      {network.network_status === 'CREATED' && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-orange-100" onClick={() => handleRelease(network.network_id)} data-testid={`release-${network.network_id}`}>
                          <PlayCircle className="w-4 h-4 text-orange-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => navigate(`/ps/execution/activities?network_id=${network.network_id}`)}>
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredNetworks.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-500">Showing <span className="font-medium text-slate-700">{filteredNetworks.length}</span> networks</p>
            </div>
          )}
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Network</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Network ID (auto-generated if empty)</Label>
                <Input
                  value={formData.network_id}
                  onChange={(e) => setFormData({ ...formData, network_id: e.target.value })}
                  placeholder="e.g., NW-001"
                  data-testid="input-network-id"
                />
              </div>
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select value={formData.project_id} onValueChange={handleProjectChange}>
                  <SelectTrigger data-testid="select-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>WBS Element *</Label>
              <Select 
                value={formData.wbs_element_id} 
                onValueChange={(v) => setFormData({ ...formData, wbs_element_id: v })}
                disabled={!formData.project_id || formData.project_id === '__none__'}
              >
                <SelectTrigger data-testid="select-wbs">
                  <SelectValue placeholder="Select WBS element" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select WBS element</SelectItem>
                  {wbsElements.map(w => (
                    <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Network description"
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Finish Date</Label>
                <Input
                  type="date"
                  value={formData.finish_date}
                  onChange={(e) => setFormData({ ...formData, finish_date: e.target.value })}
                  data-testid="input-finish-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!formData.project_id || formData.project_id === '__none__' || !formData.wbs_element_id || formData.wbs_element_id === '__none__'}
              data-testid="submit-create-network"
            >
              Create Network
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Dialog */}
      <Dialog open={showViewEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowViewEditDialog(false);
          setSelectedNetwork(null);
          setIsEditMode(false);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditMode ? 'Edit Network' : 'Network Details'}</span>
              {!isEditMode && selectedNetwork?.network_status === 'CREATED' && (
                <Button size="sm" variant="outline" onClick={handleEdit} data-testid="edit-network-btn">
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedNetwork && (
            <div className="space-y-4 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Network ID</Label>
                  <div className="font-medium">{selectedNetwork.network_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div>{getStatusBadge(selectedNetwork.network_status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Project</Label>
                  <div className="font-medium">{selectedNetwork.project_name || selectedNetwork.project_id}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">WBS Element</Label>
                  <div className="font-medium">{selectedNetwork.wbs_element_name || selectedNetwork.wbs_element_id}</div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Description</Label>
                  {isEditMode ? (
                    <Input
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      data-testid="edit-description"
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{selectedNetwork.description || '-'}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Basic Start Date</Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={editFormData.basic_start_date}
                        onChange={(e) => setEditFormData({ ...editFormData, basic_start_date: e.target.value })}
                        data-testid="edit-start-date"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedNetwork.basic_start_date || '-'}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Basic Finish Date</Label>
                    {isEditMode ? (
                      <Input
                        type="date"
                        value={editFormData.basic_finish_date}
                        onChange={(e) => setEditFormData({ ...editFormData, basic_finish_date: e.target.value })}
                        data-testid="edit-finish-date"
                      />
                    ) : (
                      <p className="p-2 bg-gray-50 rounded">{selectedNetwork.basic_finish_date || '-'}</p>
                    )}
                  </div>
                </div>

                {/* Read-only info */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Activities Count</Label>
                    <p className="font-medium">{selectedNetwork.activity_count || 0}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Overall Progress</Label>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-orange-600 h-2 rounded-full" 
                          style={{ width: `${selectedNetwork.overall_progress || 0}%` }}
                        />
                      </div>
                      <span className="text-sm">{selectedNetwork.overall_progress || 0}%</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Created At</Label>
                    <p className="text-sm">{selectedNetwork.created_at ? new Date(selectedNetwork.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} data-testid="save-network-btn">Save Changes</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowViewEditDialog(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
